/**
 * SharedLiveTracking — the live-tracking page. One URL, same content for everyone
 * (owner, club members, anyone with the link) — no login required.
 *
 * Blends a race prediction with UTMB Live splits: heading with overall status,
 * the route map + last known position, the distance-based chart with a "last
 * seen" marker, and a checkpoint grid with predicted/live/delta columns.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert, useMediaQuery, useTheme } from '@mui/material';
import { tokens } from '../../theme';
import { getSharedLiveTracking } from '../../api/runningApi';
import { buildPredictionWorkoutData } from '../../utils/runningPrediction';
import LiveTrackingView from '../../components/running/live/LiveTrackingView';
import RunningRouteMap from '../../components/running/RunningRouteMap';
import RunningWorkoutChartsSection from '../../components/running/RunningWorkoutChartsSection';
import RunningCheckpointsGrid from '../../components/running/RunningCheckpointsGrid';

// 5 minutes — see docs/running_live_tracking_spec.md. Polling is demand-driven: the timer only
// runs while this page is open AND visible (see the visibilitychange handler below), and stops for
// good once the runner is finished/DNF. Nothing is fetched upstream when nobody is watching.
const POLL_INTERVAL_MS = 300000;

export default function SharedLiveTracking() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { shareId } = useParams();

  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [clockTick, setClockTick] = useState(0);
  const pollTimerRef = useRef(null);

  // The "predicted current position" moves continuously with wall-clock time, independent
  // of the 2-minute data poll — re-render periodically so it visibly creeps forward.
  useEffect(() => {
    const id = setInterval(() => setClockTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const poll = useCallback(async (isInitial = false) => {
    if (!shareId) return;
    if (isInitial) setLoading(true);
    else setRefreshing(true);
    try {
      const data = await getSharedLiveTracking(shareId);
      setSnapshot(data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load shared live tracking');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [shareId]);

  useEffect(() => {
    poll(true);

    const clearTimer = () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
    const startTimer = () => {
      clearTimer();
      pollTimerRef.current = setInterval(() => {
        setSnapshot((current) => {
          const status = current?.runner?.status || current?.link?.status;
          if (status === 'finished' || status === 'dnf') {
            clearTimer();
            return current;
          }
          poll(false);
          return current;
        });
      }, POLL_INTERVAL_MS);
    };
    const handleVisibility = () => {
      if (document.hidden) {
        clearTimer();
      } else {
        poll(false);
        startTimer();
      }
    };

    startTimer();
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearTimer();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [poll]);

  // Reuse the exact prediction -> workoutData pipeline the analysis page uses,
  // so the map/chart/grid render identically to "the route map/chart as we have it now".
  const workoutData = useMemo(() => {
    if (!snapshot?.prediction) return null;
    const prediction = snapshot.prediction;
    return buildPredictionWorkoutData(prediction, prediction.race || null, prediction.race?.race_start || undefined);
  }, [snapshot]);

  // Merge live splits into the prediction's checkpoints — both are derived from the
  // same ordered list server-side, so they line up 1:1 by index.
  const mergedCheckpoints = useMemo(() => {
    if (!workoutData?.analyzed_checkpoints) return [];
    const liveCheckpoints = snapshot?.checkpoints || [];
    return workoutData.analyzed_checkpoints.map((cp, i) => ({
      ...cp,
      live_elapsed_s: liveCheckpoints[i]?.live_elapsed_s ?? null,
      live_rank: liveCheckpoints[i]?.live_rank ?? null,
      delta_s: liveCheckpoints[i]?.delta_s ?? null,
      estimated_elapsed_s: liveCheckpoints[i]?.estimated_elapsed_s ?? null,
    }));
  }, [workoutData, snapshot]);

  const lastSeenCheckpoint = useMemo(() => {
    return [...mergedCheckpoints].reverse().find((cp) => cp.live_elapsed_s != null) || null;
  }, [mergedCheckpoints]);

  const routeLiveMarker = snapshot?.last_location?.lat != null ? {
    lat: snapshot.last_location.lat,
    lon: snapshot.last_location.lon,
    label: snapshot?.runner?.fullname || 'Last known position',
    sublabel: snapshot.last_location.source === 'gps'
      ? 'Live GPS'
      : (snapshot.last_location.checkpoint_name ? `At ${snapshot.last_location.checkpoint_name}` : undefined),
  } : null;

  const chartLiveMarker = lastSeenCheckpoint ? {
    distanceKm: lastSeenCheckpoint.fit_distance_km ?? lastSeenCheckpoint.distance_km,
    label: `Last seen: ${lastSeenCheckpoint.name}`,
  } : null;

  // Where the runner should be *right now* per the adjusted prediction — interpolated between
  // whichever checkpoints bracket "elapsed time since the real race start" (live > estimated >
  // predicted per checkpoint), then mapped to a course position via the predicted chunks.
  const predictedNowMarker = useMemo(() => {
    const startIso = snapshot?.runner?.start_time_utc;
    if (snapshot?.runner?.status !== 'live' || !startIso || !workoutData?.chunks?.length) return null;

    const nowElapsedS = (Date.now() - new Date(startIso).getTime()) / 1000;
    if (nowElapsedS <= 0) return null;

    const anchors = mergedCheckpoints
      .map((cp) => ({
        distanceKm: cp.fit_distance_km ?? cp.distance_km,
        elapsedS: cp.live_elapsed_s ?? cp.estimated_elapsed_s ?? cp.arrival_elapsed_seconds,
        stopS: cp.stopping_time_seconds || 0,
      }))
      .filter((a) => a.distanceKm != null && a.elapsedS != null)
      .sort((a, b) => a.distanceKm - b.distanceKm);
    if (anchors.length < 2) return null;

    let distanceKm = anchors[anchors.length - 1].distanceKm; // default: clamp near/at finish
    if (nowElapsedS <= anchors[0].elapsedS) {
      distanceKm = anchors[0].distanceKm;
    } else {
      for (let i = 0; i < anchors.length - 1; i++) {
        const a = anchors[i];
        const b = anchors[i + 1];
        if (nowElapsedS >= a.elapsedS && nowElapsedS <= b.elapsedS) {
          // The segment's predicted duration includes checkpoint a's own stop time — don't
          // creep the marker forward while "now" still falls inside that predicted rest
          // window, only once it's predicted to be back on the move.
          const stopEnd = a.elapsedS + a.stopS;
          if (a.stopS > 0 && nowElapsedS <= stopEnd) {
            distanceKm = a.distanceKm;
          } else {
            const moveStart = Math.max(a.elapsedS, stopEnd);
            const span = b.elapsedS - moveStart;
            const frac = span > 0 ? Math.min(1, Math.max(0, (nowElapsedS - moveStart) / span)) : 0;
            distanceKm = a.distanceKm + (b.distanceKm - a.distanceKm) * frac;
          }
          break;
        }
      }
    }

    const targetM = distanceKm * 1000;
    let nearest = null;
    let nearestDiff = Infinity;
    for (const c of workoutData.chunks) {
      if (c.lat == null || c.lon == null) continue;
      const diff = Math.abs((c.distance_start_m ?? 0) - targetM);
      if (diff < nearestDiff) {
        nearestDiff = diff;
        nearest = c;
      }
    }
    if (!nearest) return null;

    return {
      distanceKm,
      lat: nearest.lat,
      lon: nearest.lon,
      label: 'Predicted current position',
    };
    // clockTick isn't read above but is a deliberate dep — it forces this to recompute every
    // 30s so the marker creeps forward with wall-clock time between the 2-minute data polls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot, workoutData, mergedCheckpoints, clockTick]);

  // Elevation progress: climb done / left and descent left, split at the runner's current
  // position (predicted live marker, else the last seen checkpoint). Chunk loss is signed.
  const elevationInfo = useMemo(() => {
    const chunks = workoutData?.chunks;
    if (!chunks?.length) return null;
    const posKm = predictedNowMarker?.distanceKm
      ?? lastSeenCheckpoint?.fit_distance_km
      ?? lastSeenCheckpoint?.distance_km
      ?? 0;
    const splitM = posKm * 1000;
    let gainPassed = 0, gainTotal = 0, lossPassed = 0, lossTotal = 0;
    for (const c of chunks) {
      const g = Math.abs(c.elevation_gain || 0);
      const l = Math.abs(c.elevation_loss || 0);
      gainTotal += g;
      lossTotal += l;
      if ((c.distance_start_m ?? 0) < splitM) {
        gainPassed += g;
        lossPassed += l;
      }
    }
    if (gainTotal <= 0 && lossTotal <= 0) return null;
    const totalKm = (workoutData.total_distance || 0) / 1000;
    const distDone = Math.min(totalKm, Math.max(0, posKm));
    return {
      gainPassed: Math.round(gainPassed),
      gainRemaining: Math.round(Math.max(0, gainTotal - gainPassed)),
      lossPassed: Math.round(lossPassed),
      lossRemaining: Math.round(Math.max(0, lossTotal - lossPassed)),
      distDoneKm: distDone,
      distLeftKm: Math.max(0, totalKm - distDone),
    };
  }, [workoutData, predictedNowMarker, lastSeenCheckpoint]);

  if (loading) {
    return (
      <Box m={isMobile ? '10px' : '20px'} display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box m={isMobile ? '10px' : '20px'}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box m={isMobile ? '10px' : '20px'}>
      <Typography variant="h5" mb={2} color={colors.grey[100]}>
        {snapshot?.prediction?.race_name || 'Live Race Tracking'}
      </Typography>

      <LiveTrackingView
        snapshot={snapshot}
        onRefresh={() => poll(false)}
        refreshing={refreshing}
        workoutStartTime={workoutData?.workout_start_time}
        elevation={elevationInfo}
      />

      {workoutData && (
        <>
          <RunningWorkoutChartsSection
            chunks={workoutData.chunks}
            chartType="distance"
            race={workoutData.race}
            matchedCheckpoints={mergedCheckpoints}
            liveMarker={chartLiveMarker}
            predictedMarker={predictedNowMarker}
            defaultMetrics={['altitude']}
          />

          {mergedCheckpoints.length > 0 && (
            <Box mt={8}>
              <RunningCheckpointsGrid
                race={workoutData.race}
                checkpoints={mergedCheckpoints}
                workoutStartTime={workoutData.workout_start_time}
                title="Race Checkpoints"
              />
            </Box>
          )}

          {/* Route map at the end of the page. Keep the page margin around it so
              there's a strip to grab for scrolling on touch (a full-bleed map eats
              the swipe gesture). */}
          <Box mt={8}>
            <RunningRouteMap
              chunks={workoutData.chunks}
              race={workoutData.race}
              matchedCheckpoints={mergedCheckpoints}
              liveMarker={routeLiveMarker}
              predictedMarker={predictedNowMarker}
            />
          </Box>
        </>
      )}
    </Box>
  );
}
